/**
 * Header/footer state model for the HTML style editor. Six fixed slots
 * (header-left/center/right, footer-left/center/right) each carry either text
 * or an image. The renderer mutates this state; the form submit serialises it
 * into multipart form fields that the API action route knows how to parse.
 *
 * Keeping this here (with no React dependency) makes both directions trivially
 * testable and lets the panel component stay focused on rendering.
 */

import type { CaraWorkspaceFileInput, HtmlDocumentTemplateFieldsFragment } from '~/api/graphql';

export const HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT = '36';

export type HeaderFooterSlotKey =
  | 'headerLeft'
  | 'headerCenter'
  | 'headerRight'
  | 'footerLeft'
  | 'footerCenter'
  | 'footerRight';

export type HeaderFooterImageState = {
  fileId: string | null;
  file: {
    id: string;
    name: string;
    mimeType?: string | null;
    url?: string | null;
  } | null;
  upload: File | null;
  existing: CaraWorkspaceFileInput['existing'] | null;
  maxHeightPt: string;
  paddingPt: string;
  innerGapPt: string;
};

export type HeaderFooterSlotState = {
  mode: 'text' | 'image';
  text: string;
  image: HeaderFooterImageState;
};

export type HeaderFooterState = Record<HeaderFooterSlotKey, HeaderFooterSlotState>;

export type HeaderFooterSlotFromFragment = NonNullable<
  NonNullable<HtmlDocumentTemplateFieldsFragment['headerFooter']>['header']
>['left'];

export const createDefaultImageState = (): HeaderFooterImageState => ({
  fileId: null,
  file: null,
  upload: null,
  existing: null,
  maxHeightPt: HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT,
  paddingPt: '',
  innerGapPt: ''
});

export const mapSlotFromTemplate = (
  slot: HeaderFooterSlotFromFragment | null | undefined
): HeaderFooterSlotState => {
  if (slot?.image?.file?.id) {
    return {
      mode: 'image',
      text: '',
      image: {
        fileId: slot.image.file.id,
        file: slot.image.file
          ? {
              id: slot.image.file.id,
              name: slot.image.file.name,
              mimeType: slot.image.file.mimeType,
              url: slot.image.file.url ?? null
            }
          : null,
        upload: null,
        existing: null,
        maxHeightPt:
          slot.image.maxHeightPt !== null && slot.image.maxHeightPt !== undefined
            ? slot.image.maxHeightPt.toString()
            : HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT,
        paddingPt:
          slot.image.paddingPt !== null && slot.image.paddingPt !== undefined
            ? slot.image.paddingPt.toString()
            : '',
        innerGapPt:
          slot.image.innerGapPt !== null && slot.image.innerGapPt !== undefined
            ? slot.image.innerGapPt.toString()
            : ''
      }
    };
  }

  return {
    mode: 'text',
    text: slot?.text ?? '',
    image: createDefaultImageState()
  };
};

export const createEmptyHeaderFooterState = (): HeaderFooterState => ({
  headerLeft: mapSlotFromTemplate(null),
  headerCenter: mapSlotFromTemplate(null),
  headerRight: mapSlotFromTemplate(null),
  footerLeft: mapSlotFromTemplate(null),
  footerCenter: mapSlotFromTemplate(null),
  footerRight: mapSlotFromTemplate(null)
});

export const buildHeaderFooterStateFromTemplate = (
  template?: HtmlDocumentTemplateFieldsFragment
): HeaderFooterState => {
  if (!template?.headerFooter) {
    return createEmptyHeaderFooterState();
  }

  const hf = template.headerFooter;
  return {
    headerLeft: mapSlotFromTemplate(hf.header?.left),
    headerCenter: mapSlotFromTemplate(hf.header?.center),
    headerRight: mapSlotFromTemplate(hf.header?.right),
    footerLeft: mapSlotFromTemplate(hf.footer?.left),
    footerCenter: mapSlotFromTemplate(hf.footer?.center),
    footerRight: mapSlotFromTemplate(hf.footer?.right)
  };
};

export function headerFooterHasImageSlot(headerFooter: HeaderFooterState): boolean {
  return (Object.values(headerFooter) as HeaderFooterSlotState[]).some(
    (slot) =>
      slot.mode === 'image' &&
      (slot.image.fileId != null || slot.image.upload != null || slot.image.existing != null)
  );
}

/**
 * Short hint describing how the image will land relative to the page edge.
 * Returns null when there's nothing useful to add (no image yet).
 */
export function getHeaderFooterImageLayoutHint(slot: HeaderFooterSlotState): string | null {
  const edgePadding = parseFloat(slot.image.paddingPt);
  const innerGap = parseFloat(slot.image.innerGapPt);
  const hasEdgePadding = !Number.isNaN(edgePadding) && edgePadding > 0;
  const hasInnerGap = !Number.isNaN(innerGap) && innerGap > 0;
  if (!hasEdgePadding && !hasInnerGap) {
    return 'Centered in margin';
  }
  if (hasInnerGap && !hasEdgePadding) {
    return 'Anchored flush to page edge; body gap added toward content';
  }
  return `Anchored ${edgePadding || 0}pt from page edge; ${innerGap || 0}pt reserved toward content`;
}

/**
 * Serialise one slot's state into multipart form fields under the supplied
 * prefix (e.g. `headerFooter.header.left`). The matching `parseHeaderFooter`
 * action helper expects this exact field layout.
 */
export const appendHeaderFooterSlotToFormData = (
  slot: HeaderFooterSlotState,
  prefix: string,
  formData: FormData
) => {
  if (slot.mode === 'text') {
    formData.append(`${prefix}.text`, slot.text ?? '');
    formData.append(`${prefix}.image.fileId`, '');
    formData.append(`${prefix}.image.maxHeightPt`, '');
    formData.append(`${prefix}.image.paddingPt`, '');
    formData.append(`${prefix}.image.innerGapPt`, '');
    return;
  }

  const maxHeight = slot.image.maxHeightPt?.trim();
  const paddingPt = slot.image.paddingPt?.trim();
  const innerGapPt = slot.image.innerGapPt?.trim();
  formData.append(`${prefix}.text`, '');
  if (slot.image.upload) {
    formData.append(`${prefix}.image.upload`, slot.image.upload);
    formData.append(`${prefix}.image.fileId`, '');
  } else if (slot.image.existing) {
    formData.append(`${prefix}.image.fileId`, slot.image.existing.id);
  } else {
    formData.append(`${prefix}.image.fileId`, slot.image.fileId ?? '');
  }
  formData.append(
    `${prefix}.image.maxHeightPt`,
    maxHeight !== '' ? maxHeight : HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT
  );
  formData.append(`${prefix}.image.paddingPt`, paddingPt ?? '');
  formData.append(`${prefix}.image.innerGapPt`, innerGapPt ?? '');
};

/**
 * Stable ordering used when serialising the whole `HeaderFooterState`. Splitting
 * the prefix construction out lets components and tests share the same field
 * paths without copy-paste.
 */
export const HEADER_FOOTER_FIELD_PREFIX: Record<HeaderFooterSlotKey, string> = {
  headerLeft: 'headerFooter.header.left',
  headerCenter: 'headerFooter.header.center',
  headerRight: 'headerFooter.header.right',
  footerLeft: 'headerFooter.footer.left',
  footerCenter: 'headerFooter.footer.center',
  footerRight: 'headerFooter.footer.right'
};

export const appendHeaderFooterToFormData = (
  headerFooter: HeaderFooterState,
  formData: FormData
): void => {
  (Object.keys(HEADER_FOOTER_FIELD_PREFIX) as HeaderFooterSlotKey[]).forEach((slotKey) => {
    appendHeaderFooterSlotToFormData(
      headerFooter[slotKey],
      HEADER_FOOTER_FIELD_PREFIX[slotKey],
      formData
    );
  });
};

/**
 * Map a slot key to the validation field path expected by the API. Used to
 * surface field-level validation errors next to the correct input.
 */
export function getSlotFieldPath(slotKey: HeaderFooterSlotKey): string[] {
  if (slotKey.startsWith('header')) {
    const position = slotKey.replace('header', '').toLowerCase();
    return ['headerFooter', 'header', position];
  }
  if (slotKey.startsWith('footer')) {
    const position = slotKey.replace('footer', '').toLowerCase();
    return ['headerFooter', 'footer', position];
  }
  return ['headerFooter'];
}

export type HeaderFooterSlotPaths = {
  /** Root path for the slot, e.g. ['headerFooter','header','left']. */
  field: string[];
  /** Sub-path for the slot's text input. */
  text: string[];
  /** Sub-path covering the slot's image (used to clear all image-related errors). */
  image: string[];
  /** Sub-path for the slot's image file upload. */
  imageUpload: string[];
  /** Sub-path for the slot's image max-height input. */
  imageMaxHeight: string[];
};

/**
 * Build every validation-field path used by a single header/footer slot. The
 * slot component renders inputs for these paths; the parent panel passes them
 * back into `clearValidationErrorForField`/`clearValidationErrorsWithPrefix`
 * when the user edits the corresponding input. Centralising the derivation
 * here ensures the slot and the parent stay in sync.
 */
export function getHeaderFooterSlotPaths(slotKey: HeaderFooterSlotKey): HeaderFooterSlotPaths {
  const field = getSlotFieldPath(slotKey);
  const image = [...field, 'image'];
  return {
    field,
    text: [...field, 'text'],
    image,
    imageUpload: [...image, 'upload'],
    imageMaxHeight: [...image, 'maxHeightPt']
  };
}

export function validationFieldHasPrefix(field: string[], prefix: string[]): boolean {
  return field.length >= prefix.length && prefix.every((v, i) => field[i] === v);
}
